import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Briefcase, MapPin, Clock, Users, Heart, Zap, Award, Coffee, ArrowRight } from 'lucide-react';

const openPositions = [
  {
    title: 'Network Engineer',
    department: 'ICT Solutions',
    location: 'Chennai',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Looking for experienced network engineers with Cisco/Juniper certifications.'
  },
  {
    title: 'System Administrator',
    department: 'Cloud & Data Centre',
    location: 'Bangalore',
    type: 'Full-time',
    experience: '2-4 years',
    description: 'Manage and maintain enterprise server infrastructure and cloud environments.'
  },
  {
    title: 'Cyber Security Analyst',
    department: 'Cyber Security',
    location: 'Chennai',
    type: 'Full-time',
    experience: '3-6 years',
    description: 'Conduct security assessments, VAPT, and implement security solutions.'
  },
  {
    title: 'Sales Executive - IT Solutions',
    department: 'Sales',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '2-5 years',
    description: 'Drive sales for ICT and software solutions in enterprise segment.'
  },
  {
    title: 'Technical Support Engineer',
    department: 'Support',
    location: 'Cochin',
    type: 'Full-time',
    experience: '1-3 years',
    description: 'Provide L1/L2 technical support for enterprise customers.'
  },
];

const benefits = [
  { icon: Heart, title: 'Health Insurance', description: 'Comprehensive medical coverage for you and your family' },
  { icon: Zap, title: 'Learning & Growth', description: 'Certification sponsorship and training programs' },
  { icon: Coffee, title: 'Work-Life Balance', description: 'Flexible hours and remote work options' },
  { icon: Award, title: 'Recognition', description: 'Performance bonuses and awards program' },
  { icon: Users, title: 'Great Team', description: 'Collaborative and supportive work environment' },
  { icon: Briefcase, title: 'Career Growth', description: 'Clear career paths and promotion opportunities' },
];

export const CareersPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Careers - Felix Infotech | Join Our Growing IT Team</title>
        <meta name="description" content="Join Felix Infotech's growing team. Explore career opportunities in Network Engineering, Cyber Security, Cloud, System Administration, and IT Sales across India." />
        <meta name="keywords" content="Felix Infotech careers, IT jobs Chennai, network engineer jobs, cybersecurity jobs India, system administrator vacancy, IT sales jobs" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/careers" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Careers at Felix Infotech | Join Our Team" />
        <meta property="og:description" content="Explore IT career opportunities in networking, cybersecurity, cloud, and more." />
        <meta property="og:url" content="https://felixinfotech.com/careers" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Careers at Felix Infotech" />
        <meta name="twitter:description" content="Join our team of 100+ IT professionals. Open positions across India." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Build Your Career With Us"
          subtitle="Join Our Team"
          description="Be part of a dynamic team that's shaping the future of technology solutions in India and beyond."
          badge="Careers"
          animationType="about"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'Careers' }]}
        />

        {/* Why Join Us */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Join Felix Infotech?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We offer a dynamic work environment where innovation meets opportunity.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#0066CC]/10 flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-[#0066CC]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Open Positions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Explore current opportunities and find your perfect role.
              </p>
            </motion.div>

            <div className="space-y-4 max-w-4xl mx-auto">
              {openPositions.map((job, index) => (
                <motion.div
                  key={job.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{job.description}</p>
                      <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Briefcase className="w-3 h-3" /> {job.department}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <MapPin className="w-3 h-3" /> {job.location}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" /> {job.experience}
                        </span>
                      </div>
                    </div>
                    <Link to="/contact">
                      <Button className="bg-[#0066CC] hover:bg-[#0055AA] text-white">
                        Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <p className="text-gray-600 mb-4">Don't see a position that fits? Send us your resume anyway!</p>
              <Link to="/contact">
                <Button variant="outline" className="border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC] hover:text-white">
                  Submit Your Resume
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default CareersPage;
